import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Edit, GripVertical, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CategoryFormModal } from '@/components/admin/CategoryFormModal';
import { ConfirmModal } from '@/components/admin/ConfirmModal';
import { Button } from '@/components/ui/button';
import { useAdminFetch } from '@/hooks/useAdminFetch';
import { parseApiError } from '@/lib/apiError';

interface Category {
  id: number;
  name: string;
  slug: string;
  description?: string;
  order: number;
}

function SortableRow({
  category,
  onEdit,
  onDelete,
}: {
  category: Category;
  onEdit: (id: string) => void;
  onDelete: (cat: Category) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b border-gray-800 hover:bg-gray-800/50 transition-colors ${
        isDragging ? 'bg-gray-800 opacity-50 shadow-2xl relative z-10' : ''
      }`}
    >
      <td className="px-6 py-4 w-10">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-600 hover:text-gray-400 p-1 rounded-md transition-colors"
        >
          <GripVertical className="size-4" />
        </button>
      </td>
      <td className="px-6 py-4 text-white font-medium">{category.name}</td>
      <td className="px-6 py-4 text-gray-400 font-mono text-sm">{category.slug}</td>
      <td className="px-6 py-4 text-gray-400 text-sm italic">{category.description || '-'}</td>
      <td className="px-6 py-4 text-right">
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="icon" onClick={() => onEdit(String(category.id))}>
            <Edit className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(category)}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

export const Route = createFileRoute('/admin/posts/categories')({
  component: AdminCategoriesPage,
});

function AdminCategoriesPage() {
  const { fetchAdmin } = useAdminFetch();
  const queryClient = useQueryClient();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState<string | null>(null);
  const [deleteCategory, setDeleteCategory] = useState<{ id: number; name: string } | null>(null);
  const [localCategories, setLocalCategories] = useState<Category[]>([]);

  const { data: categories, isLoading } = useQuery<Category[]>({
    queryKey: ['admin-categories'],
    queryFn: async () => {
      const res = await fetchAdmin('/api/admin/blog/categories');
      if (!res.ok) throw new Error('Failed to fetch categories');
      const json = await res.json();
      return json.data;
    },
  });

  useEffect(() => {
    if (categories) {
      setLocalCategories(categories);
    }
  }, [categories]);

  const reorderMutation = useMutation({
    mutationFn: async (orders: { id: number; order: number }[]) => {
      const res = await fetchAdmin('/api/admin/blog/categories/reorder', {
        method: 'POST',
        body: JSON.stringify({ orders }),
      });
      if (!res.ok) {
        const message = await parseApiError(res, 'Cập nhật thứ tự thất bại');
        throw new Error(message);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      toast.success('Đã cập nhật thứ tự');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Cập nhật thứ tự thất bại');
      // Reset local state on error
      if (categories) setLocalCategories(categories);
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = localCategories.findIndex((c) => c.id === active.id);
      const newIndex = localCategories.findIndex((c) => c.id === over.id);

      const newCategories = arrayMove(localCategories, oldIndex, newIndex);
      setLocalCategories(newCategories);

      // Prepare orders for backend
      const orders = newCategories.map((c, index) => ({
        id: c.id,
        order: index,
      }));

      await reorderMutation.mutateAsync(orders);
    }
  };

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetchAdmin(`/api/admin/blog/categories/${id}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const message = await parseApiError(res, 'Xóa thất bại');
        throw new Error(message);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-categories'] });
      toast.success('Đã xóa chuyên mục');
      setDeleteCategory(null);
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Xóa thất bại');
    },
  });

  const handleConfirmDelete = async () => {
    if (!deleteCategory) return;
    await deleteMutation.mutateAsync(deleteCategory.id);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">Quản lý Chuyên mục</h1>
        <Button
          onClick={() => setCreateModalOpen(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white border-0 shadow-lg shadow-purple-500/20"
        >
          <Plus className="size-4" />
          Thêm chuyên mục
        </Button>
      </div>

      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="px-6 py-4 w-10"></th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Tên</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Slug</th>
                  <th className="text-left px-6 py-4 text-sm font-medium text-gray-400">Mô tả</th>
                  <th className="text-right px-6 py-4 text-sm font-medium text-gray-400">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500 mx-auto" />
                    </td>
                  </tr>
                ) : localCategories.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                      Chưa có chuyên mục nào
                    </td>
                  </tr>
                ) : (
                  <SortableContext
                    items={localCategories.map((c) => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {localCategories.map((category) => (
                      <SortableRow
                        key={category.id}
                        category={category}
                        onEdit={setEditCategoryId}
                        onDelete={setDeleteCategory}
                      />
                    ))}
                  </SortableContext>
                )}
              </tbody>
            </table>
          </DndContext>
        </div>
      </div>

      <CategoryFormModal open={createModalOpen} onOpenChange={setCreateModalOpen} />
      <CategoryFormModal
        open={Boolean(editCategoryId)}
        onOpenChange={(open) => !open && setEditCategoryId(null)}
        categoryId={editCategoryId}
      />

      <ConfirmModal
        open={Boolean(deleteCategory)}
        onOpenChange={(open) => !open && setDeleteCategory(null)}
        title="Xóa chuyên mục"
        description={
          deleteCategory
            ? `Bạn có chắc muốn xóa chuyên mục "${deleteCategory.name}"? Các bài viết thuộc chuyên mục này sẽ không bị xóa nhưng sẽ mất liên kết với chuyên mục.`
            : undefined
        }
        confirmLabel="Xóa"
        cancelLabel="Hủy"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
